/**
 * LiveTestingBodySelectField 컴포넌트
 *
 * Body Selection 서브타입들(body_select_back, body_select_belly 등)을 위한 Live Testing 필드
 * V1의 SVG 버튼 컴포넌트들을 재사용
 */

import produce from 'immer';
import { last, truncate, uniq, upperCase } from 'lodash';
import { memo, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { BsFillPlusCircleFill, BsPlusCircle } from 'react-icons/bs';
import { FaArrowLeft } from 'react-icons/fa';
import { FaArrowsUpDown } from 'react-icons/fa6';
import { MdClose } from 'react-icons/md';
import {
  useStaticLocales,
  type SystemLocaleCodes,
  type SystemLocaleKeys,
} from '../../../../../admin/hooks/locales/use-static-locale';
import { useTranslations } from '../../../../../patient/hooks/use-translations';
import { QuestionResponseDto } from '../../../../api/generated/types';
import LiveTestingContainer from './LiveTestingContainer';
import { getLocaleCode } from '../../../organization/configurations/language/constants';

// V1 SVG 버튼 컴포넌트들 import
import BackFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BackFemaleButtons';
import BackLowerFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BackLowerFemaleButtons';
import BackLowerMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BackLowerMaleButtons';
import BackMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BackMaleButtons';
import BackUpperFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BackUpperFemaleButtons';
import BackUpperMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BackUpperMaleButtons';
import BellyFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BellyFemaleButtons';
import BellyMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BellyMaleButtons';
import BodyFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BodyFemaleButtons';
import BodyMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/BodyMaleButtons';
import ChestFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/ChestFemaleButtons';
import ChestMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/ChestMaleButtons';
import DentalLowerButtons from '../../../../../patient/question/typed-qs/body-select/buttons/DentalLowerButtons';
import DentalUpperButtons from '../../../../../patient/question/typed-qs/body-select/buttons/DentalUpperButtons';
import FaceFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/FaceFemaleButtons';
import FaceMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/FaceMaleButtons';
import LimbsFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/LimbsFemaleButtons';
import LimbsMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/LimbsMaleButtons';
import MouthButtons from '../../../../../patient/question/typed-qs/body-select/buttons/MouthButtons';
import MouthLiftButtons from '../../../../../patient/question/typed-qs/body-select/buttons/MouthLiftButtons';
import SensoryFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/SensoryFemaleButtons';
import SensoryMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/SensoryMaleButtons';
import LeftArmFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/LeftArmFemaleButtons';
import LeftArmMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/LeftArmMaleButtons';
import RightArmFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/RightArmFemaleButtons';
import RightArmMaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/RightArmMaleButtons';
import LegLeftButtons from '../../../../../patient/question/typed-qs/body-select/buttons/LegLeftButtons';
import LegRightButtons from '../../../../../patient/question/typed-qs/body-select/buttons/LegRightButtons';
import LegLeftFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/LegLeftFemaleButtons';
import LegRightFemaleButtons from '../../../../../patient/question/typed-qs/body-select/buttons/LegRightFemaleButtons';

// Body Selection 서브타입 매핑
const BODY_SELECT_TYPES = {
  body_select_belly: 'belly',
  body_select_chest: 'chest',
  body_select_back: 'back',
  body_select_face: 'face',
  body_select_head: 'head',
  body_select_limbs: 'limbs',
  body_select_mouth: 'mouth',
  body_select_dental: 'dental',
  body_select_sensory: 'sensory',
  body_select_whole: 'whole',
  body_select_subpart: 'subpart',
  body_select_arm: 'arm',
  body_select_leg: 'leg',
  body_select_joints: 'limbs',
} as const;

type BodySelectType = keyof typeof BODY_SELECT_TYPES;

export interface BodySelectValue {
  answerKeys: string[];
  notesForKeys?: Record<string, string>;
}

interface LiveTestingBodySelectFieldProps {
  question: QuestionResponseDto;
  value: BodySelectValue;
  onChange: (value: BodySelectValue) => void;
  disabled?: boolean;
  isMale?: boolean;
}

/**
 * Live Testing용 Body Selection 필드
 */
// Back 타입의 네비게이션 키
const BACK_NAV_KEYS = ['o-back-upper', 'o-back-lower'];

function LiveTestingBodySelectField({
  question,
  value,
  onChange,
  disabled = false,
  isMale = true,
}: LiveTestingBodySelectFieldProps) {
  const { currentLocale } = LiveTestingContainer.useContainer();
  const localeCode = useMemo(
    () => getLocaleCode(currentLocale || 'english') as SystemLocaleCodes,
    [currentLocale]
  );
  const { sltStr } = useStaticLocales({ localeCode });
  const [, { t }] = useTranslations({ locale: currentLocale });
  const { inputType, answerOptionsMap } = question;
  const bodyType = BODY_SELECT_TYPES[inputType as BodySelectType];

  const selectedKeys = useMemo(() => value?.answerKeys || [], [value?.answerKeys]);

  // Back 타입을 위한 네비게이션 스택 (V1과 동일)
  const [selectedStacks, setSelectedStacks] = useState<string[]>([]);

  // Dental 타입을 위한 UPPER/LOWER 세그먼트 토글 (V1 스타일)
  const [dentalLower, setDentalLower] = useState(false);

  // Mouth 타입을 위한 Tongue Lift 토글 (V1 스타일)
  const [tongueLift, setTongueLift] = useState(false);
  const [armSide, setArmSide] = useState<'left' | 'right'>('left');
  const [legSide, setLegSide] = useState<'left' | 'right'>('left');
  const selectedStack = last(selectedStacks);
  const trWithStaticFallback = useCallback(
    (key: string, fallbackValue: string, staticKey: SystemLocaleKeys) => {
      const translated = t(key, null, { fallbackValue: null });
      if (translated && translated !== key) {
        return translated;
      }
      const staticTranslated = sltStr({ key: staticKey });
      if (staticTranslated && staticTranslated !== staticKey) {
        return staticTranslated;
      }
      return fallbackValue;
    },
    [sltStr, t]
  );

  // 상호배타적 옵션 키 추출
  const mutExcAnswerKeys = useMemo(() => {
    if (!answerOptionsMap) return [];
    return Object.entries(answerOptionsMap)
      .filter(([_, opt]) => (opt as any)?.mutuallyExclusive)
      .map(([key]) => key);
  }, [answerOptionsMap]);

  // Main Option 키 추출 (예: O MOUTH WHOLE, O MOUTH OTHER)
  const mainOptionKeys = useMemo(() => {
    if (!answerOptionsMap) return [];
    return Object.entries(answerOptionsMap)
      .filter(([_, opt]) => (opt as any)?.mainOption)
      .map(([key]) => key);
  }, [answerOptionsMap]);

  // requireNote 옵션 키 추출 (노트 입력이 필요한 옵션)
  const requireNoteKeys = useMemo(() => {
    if (!answerOptionsMap) return [];
    return Object.entries(answerOptionsMap)
      .filter(([_, opt]) => (opt as any)?.requireNote)
      .map(([key]) => key);
  }, [answerOptionsMap]);

  // requireNote 입력 상태 (V1과 동일)
  const [requireNoteKey, setRequireNoteKey] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');

  // 클릭 핸들러
  const handleClick = useCallback(
    ({ id, note }: { id: string; note?: string }) => {
      if (disabled) return;

      const selected = selectedKeys.includes(id);
      const isMutExcl = mutExcAnswerKeys.includes(id);

      let nKeys: string[] = [...selectedKeys];

      if (isMutExcl) {
        // 상호배타적 옵션: 해당 옵션만 선택
        if (selected) {
          nKeys = [];
        } else {
          nKeys = [id];
        }
      } else {
        // 일반 옵션: 상호배타적 옵션 제외하고 토글
        nKeys = nKeys.filter((k) => !mutExcAnswerKeys.includes(k));
        if (selected) {
          nKeys = nKeys.filter((i) => i !== id);
        } else {
          nKeys = uniq([...nKeys, id]);
        }
      }

      const nValue = produce(value || { answerKeys: [] }, (dr) => {
        dr.answerKeys = nKeys;
        // 노트가 있으면 notesForKeys에 저장
        if (note !== undefined) {
          if (!dr.notesForKeys) {
            dr.notesForKeys = {};
          }
          dr.notesForKeys[id] = note;
        }
      });

      onChange(nValue);
    },
    [selectedKeys, mutExcAnswerKeys, disabled, value, onChange]
  );

  // mainOption 클릭 핸들러 (requireNote 체크)
  const handleMainOptionClick = useCallback(
    (key: string) => {
      if (disabled) return;

      const selected = selectedKeys.includes(key);
      const isRequireNote = requireNoteKeys.includes(key);

      // 이미 선택된 경우 - 선택 해제
      if (selected) {
        handleClick({ id: key });
        return;
      }

      // requireNote가 필요한 경우 - 입력 필드 표시
      if (isRequireNote) {
        setRequireNoteKey(key);
        setNoteText(value?.notesForKeys?.[key] || '');
        return;
      }

      // 일반 선택
      handleClick({ id: key });
    },
    [disabled, selectedKeys, requireNoteKeys, handleClick, value?.notesForKeys]
  );

  // 노트 추가 완료 핸들러 (ADD 버튼)
  const handleAddNote = useCallback(() => {
    if (!requireNoteKey) return;

    // 노트와 함께 선택
    handleClick({ id: requireNoteKey, note: noteText });

    // 입력 상태 초기화
    setRequireNoteKey(null);
    setNoteText('');
  }, [requireNoteKey, noteText, handleClick]);

  // 노트 입력 취소 핸들러
  const handleCancelNote = useCallback(() => {
    setRequireNoteKey(null);
    setNoteText('');
  }, []);

  // SVG 클릭 핸들러 (일반 body type용 - 바로 선택)
  const handleSvgClick = useCallback(
    (e: React.MouseEvent) => {
      const el = e.target as HTMLElement;
      const id = el.getAttribute('id');
      if (id) {
        handleClick({ id });
      }
    },
    [handleClick]
  );

  // Back 네비게이션 클릭 핸들러 (상세 화면으로 이동)
  const handleNavItemClick = useCallback(
    (e: React.MouseEvent) => {
      const el = e.target as HTMLElement;
      const id = el.getAttribute('id');
      if (id && BACK_NAV_KEYS.includes(id)) {
        setSelectedStacks([id]);
      }
    },
    []
  );

  // Back 리프 아이템 클릭 핸들러 (실제 부위 선택)
  const handleLeafItemClick = useCallback(
    (e: React.MouseEvent) => {
      const el = e.target as HTMLElement;
      const id = el.getAttribute('id');
      if (id) {
        handleClick({ id });
      }
    },
    [handleClick]
  );

  // 뒤로가기 핸들러
  const handleBackClick = useCallback(() => {
    setSelectedStacks([]);
  }, []);

  // 옵션 라벨 가져오기 (V1과 동일: useTranslations의 t() 사용)
  const getOptionLabel = useCallback(
    (key: string): string => {
      // 1. 먼저 answerOptionsMap에서 label 찾기
      const optionData = answerOptionsMap?.[key] as Record<string, unknown> | undefined;
      if (optionData?.label) {
        return optionData.label as string;
      }
      // 2. 없으면 translations에서 찾기 (V1과 동일)
      const translated = t(key);
      return translated || key;
    },
    [answerOptionsMap, t]
  );

  // SVG 버튼 컴포넌트 렌더링
  const renderBodySelector = () => {
    const commonProps = {
      selectedKeys,
      className: 'relative max-w-full',
      onClick: handleSvgClick,
    };

    switch (bodyType) {
      case 'belly':
        return isMale ? (
          <BellyMaleButtons {...commonProps} />
        ) : (
          <BellyFemaleButtons {...commonProps} />
        );

      case 'chest':
        return isMale ? (
          <ChestMaleButtons {...commonProps} />
        ) : (
          <ChestFemaleButtons {...commonProps} />
        );

      case 'back':
        // Back은 네비게이션 스택이 있음 (V1과 동일)
        // 초기 화면: BackMaleButtons/BackFemaleButtons (네비게이션용)
        // 상세 화면: BackUpperMaleButtons/BackLowerMaleButtons 등 (선택용)
        if (!selectedStack) {
          // 초기 화면 - 네비게이션 버튼
          return isMale ? (
            <BackMaleButtons
              {...commonProps}
              subQuestionKeys={BACK_NAV_KEYS}
              onClick={handleNavItemClick}
            />
          ) : (
            <BackFemaleButtons
              {...commonProps}
              subQuestionKeys={BACK_NAV_KEYS}
              onClick={handleNavItemClick}
            />
          );
        }
        if (selectedStack === 'o-back-upper') {
          // 등 상단 상세 화면
          return isMale ? (
            <BackUpperMaleButtons
              {...commonProps}
              onClick={handleLeafItemClick}
            />
          ) : (
            <BackUpperFemaleButtons
              {...commonProps}
              onClick={handleLeafItemClick}
            />
          );
        }
        if (selectedStack === 'o-back-lower') {
          // 등 하단 상세 화면
          return isMale ? (
            <BackLowerMaleButtons
              {...commonProps}
              onClick={handleLeafItemClick}
            />
          ) : (
            <BackLowerFemaleButtons
              {...commonProps}
              onClick={handleLeafItemClick}
            />
          );
        }
        return null;

      case 'face':
        return isMale ? (
          <FaceMaleButtons {...commonProps} />
        ) : (
          <FaceFemaleButtons {...commonProps} />
        );

      case 'limbs':
        return isMale ? (
          <LimbsMaleButtons {...commonProps} />
        ) : (
          <LimbsFemaleButtons {...commonProps} />
        );

      case 'mouth':
        return (
          <div className="flex flex-col items-center gap-2">
            {tongueLift ? (
              <MouthLiftButtons {...commonProps} />
            ) : (
              <MouthButtons {...commonProps} />
            )}
            <button
              type="button"
              className="btn btn-sm gap-2"
              onClick={() => setTongueLift(!tongueLift)}
            >
              <FaArrowsUpDown />
              {!tongueLift
                ? sltStr({ key: 'btn-lift-tongue', upper: true })
                : sltStr({ key: 'btn-lower-tongue', upper: true })}
            </button>
          </div>
        );

      case 'dental':
        // V1 스타일: UPPER/LOWER 세그먼트 토글로 전환
        return (
          <div className="flex flex-col items-center gap-2 pt-6">
            {/* 선택된 세그먼트 렌더링 */}
            {!dentalLower ? (
              <DentalUpperButtons {...commonProps} />
            ) : (
              <DentalLowerButtons {...commonProps} />
            )}
            {/* LEFT/RIGHT 레이블 + UPPER/LOWER 토글 버튼 (V1 스타일 - 하단 배치) */}
            <div className="flex justify-between w-full opacity-60 pointer-events-none text-xs">
              <span className="p-2">LEFT</span>
              <span className="p-2">RIGHT</span>
            </div>
            <div className="btn-group flex">
              <button
                type="button"
                className={classNames(
                  'btn btn-sm',
                  !dentalLower ? 'bg-[#4B7BF5] text-white' : 'bg-[#EAF1FF] text-[#4B7BF5]'
                )}
                onClick={() => setDentalLower(false)}
              >
                UPPER
              </button>
              <button
                type="button"
                className={classNames(
                  'btn btn-sm',
                  dentalLower ? 'bg-[#4B7BF5] text-white' : 'bg-[#EAF1FF] text-[#4B7BF5]'
                )}
                onClick={() => setDentalLower(true)}
              >
                LOWER
              </button>
            </div>
          </div>
        );

      case 'whole':
      case 'subpart':
        return isMale ? (
          <BodyMaleButtons {...commonProps} />
        ) : (
          <BodyFemaleButtons {...commonProps} />
        );

      case 'head':
        // head는 whole body와 유사하게 처리
        return isMale ? (
          <BodyMaleButtons {...commonProps} />
        ) : (
          <BodyFemaleButtons {...commonProps} />
        );

      case 'sensory': {
        // sensory는 전용 SensoryButtons 사용
        // alternateAnswerKeys: head 영역 클릭시 face로 매핑 (V1과 동일)
        const sensoryAlternateKeys = new Map<string, string>();
        return isMale ? (
          <SensoryMaleButtons {...commonProps} alternateAnswerKeys={sensoryAlternateKeys} />
        ) : (
          <SensoryFemaleButtons {...commonProps} alternateAnswerKeys={sensoryAlternateKeys} />
        );
      }

      case 'arm': {
        const renderArmButtons = () => {
          if (armSide === 'left') {
            return isMale ? (
              <LeftArmMaleButtons {...commonProps} />
            ) : (
              <LeftArmFemaleButtons {...commonProps} />
            );
          }
          return isMale ? (
            <RightArmMaleButtons {...commonProps} />
          ) : (
            <RightArmFemaleButtons {...commonProps} />
          );
        };
        return (
          <div className="flex flex-col items-center gap-3">
            <div className="btn-group flex">
              <button
                type="button"
                className={classNames(
                  'btn btn-sm',
                  armSide === 'left' ? 'bg-[#4B7BF5] text-white' : 'bg-[#EAF1FF] text-[#4B7BF5]'
                )}
                onClick={() => setArmSide('left')}
              >
                {sltStr({ key: 'label-left', upper: true })}
              </button>
              <button
                type="button"
                className={classNames(
                  'btn btn-sm',
                  armSide === 'right' ? 'bg-[#4B7BF5] text-white' : 'bg-[#EAF1FF] text-[#4B7BF5]'
                )}
                onClick={() => setArmSide('right')}
              >
                {sltStr({ key: 'label-right', upper: true })}
              </button>
            </div>
            {renderArmButtons()}
          </div>
        );
      }

      case 'leg': {
        const renderLegButtons = () => {
          if (legSide === 'left') {
            return isMale ? (
              <LegLeftButtons {...commonProps} />
            ) : (
              <LegLeftFemaleButtons {...commonProps} />
            );
          }
          return isMale ? (
            <LegRightButtons {...commonProps} />
          ) : (
            <LegRightFemaleButtons {...commonProps} />
          );
        };
        return (
          <div className="flex flex-col items-center gap-3">
            <div className="btn-group flex">
              <button
                type="button"
                className={classNames(
                  'btn btn-sm',
                  legSide === 'left' ? 'bg-[#4B7BF5] text-white' : 'bg-[#EAF1FF] text-[#4B7BF5]'
                )}
                onClick={() => setLegSide('left')}
              >
                {sltStr({ key: 'label-left', upper: true })}
              </button>
              <button
                type="button"
                className={classNames(
                  'btn btn-sm',
                  legSide === 'right' ? 'bg-[#4B7BF5] text-white' : 'bg-[#EAF1FF] text-[#4B7BF5]'
                )}
                onClick={() => setLegSide('right')}
              >
                {sltStr({ key: 'label-right', upper: true })}
              </button>
            </div>
            {renderLegButtons()}
          </div>
        );
      }

      default:
        return (
          <div className="p-4 text-sm text-gray-500 bg-gray-100 rounded">
            Body selector for type &quot;{bodyType}&quot; is not yet implemented
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Main Option 버튼 (예: O MOUTH WHOLE, O MOUTH OTHER) - V1 스타일 */}
      {mainOptionKeys.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {mainOptionKeys.map((key) => {
            const selected = selectedKeys.includes(key);
            return (
              <button
                key={key}
                type="button"
                className="flex-1 btn-base bg-[#EAF1FF] text-[#4B7BF5] hover:bg-[#DCE8FF]"
                disabled={disabled}
                onClick={() => handleMainOptionClick(key)}
              >
                {selected ? (
                  <BsPlusCircle size={22} className="text-[#4B7BF5] rotate-45" />
                ) : (
                  <BsFillPlusCircleFill size={22} className="text-[#4B7BF5]" />
                )}
                <span className="text-base text-[#4B7BF5]">{upperCase(getOptionLabel(key))}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* requireNote 입력 필드 (V1과 동일 - AREA DESCRIPTION) */}
      {requireNoteKey && (
        <div className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <label className="text-sm font-medium text-gray-700">
            {upperCase(
              trWithStaticFallback('label-area-description', 'area description', 'label-area-description')
            )}
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              className="flex-1 input input-bordered input-sm"
              placeholder={trWithStaticFallback(
                'label-describe-in-detail',
                'Describe in detail',
                'label-describe-in-detail'
              )}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={handleCancelNote}
            >
              {trWithStaticFallback('btn-cancel', 'Cancel', 'btn-cancel')}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={handleAddNote}
              disabled={!noteText.trim()}
            >
              {upperCase(trWithStaticFallback('btn-add', 'add', 'btn-add'))}
            </button>
          </div>
        </div>
      )}

      {/* Back 타입: 뒤로가기 버튼 및 현재 위치 표시 (V1과 동일) */}
      {bodyType === 'back' && selectedStack && (
        <div className="pb-2">
          <button
            type="button"
            className="cursor-pointer flex items-center gap-3 text-sm hover:opacity-80 transition-opacity"
            onClick={handleBackClick}
          >
            <FaArrowLeft size={13} />
            <span className="opacity-80">{getOptionLabel(selectedStack)}</span>
          </button>
        </div>
      )}

      {/* 신체 부위 SVG with LEFT/RIGHT labels */}
      <div className="relative flex justify-center pt-6">
        {/* LEFT/RIGHT 라벨 (V1과 동일) - dental은 내부에서 처리하므로 외부에서 숨김 */}
        {bodyType !== 'dental' && (
          <div className="absolute flex-center top-0 w-full flex justify-between opacity-60 pointer-events-none">
            <span className="p-5 text-sm">{sltStr({ key: 'label-left', upper: true })}</span>
            <span className="p-5 text-sm">{sltStr({ key: 'label-right', upper: true })}</span>
          </div>
        )}
        {renderBodySelector()}
      </div>

      {/* 선택된 부위 표시 */}
      {selectedKeys.length > 0 && (
        <div className="flex flex-wrap gap-3 pt-4 border-t">
          {selectedKeys.map((key) => {
            const label = getOptionLabel(key);
            const note = value?.notesForKeys?.[key];
            return (
              <button
                key={key}
                type="button"
                className="btn btn-outline btn-primary border-primary p-2 min-h-9 max-w-full flex gap-2"
                onClick={() => handleClick({ id: key })}
                disabled={disabled}
              >
                <span className="uppercase">{label}</span>
                {note && ` (${truncate(note, { length: 75 })})`}
                <MdClose size={16} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default memo(LiveTestingBodySelectField);
